import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import ColumnToggle from "./ColumnToggle";
import BulkActions from "../../../../Home/Actions/BulkActions";
import Download from "../../../../Home/Actions/Download";
import Button from "../../../../../../components/Button";
import { useTableContext } from "../../../../../../components/Table/context";
import { useExport } from "../../../hooks/useExport";
import { useAccess } from "../../../../../../utils/hooks/access";

const Actions = ({
    user,
    filters,
    viewSummary,
    retroAdjustments,
    byPayDateRetros,
    openRetroModal,
    payGroupItems,
    mutateRetroAdjustment,
    refetchTimeSheets,
    openDetails,
    ...props
}) => {
    const { t } = useTranslation();
    const { hasAccess: canCreateRetro } = useAccess("timeSheet.canViewRetro");
    const {
        columns,
        state: { selectedRowIds },
    } = useTableContext();

    const ids = useMemo(() => Object.keys(selectedRowIds), [selectedRowIds]);
    const columnKeys = useMemo(
        () => columns.map(({ id }) => id)?.filter((c) => !!c),
        [columns],
    );
    const { downloadSummary, downloadDetails } = useExport({
        ids,
        columns: columnKeys,
        filters,
        other: { userId: user?.id },
    });

    const lockedPayGroupItems = useMemo(() => {
        return payGroupItems?.filter((payGroup) => payGroup.status === "locked");
    }, [payGroupItems]);

    const retros = useMemo(() => {
        return retroAdjustments?.reduce((total, item) => {
            if (item?.retros?.length > 0) {
                total.push(item.retros);
            }
            return total.flat();
        }, []);
    }, [retroAdjustments]);

    const byPayDateRetro = useMemo(() => {
        return byPayDateRetros?.reduce((total, item) => {
            if (item?.retros?.length > 0) {
                total.push(item.retros);
            }
            return total.flat();
        }, []);
    }, [byPayDateRetros]);

    return (
        <div className="d-flex justify-content-between align-items-center">
            <div className="flex items-center gap-2">
                <BulkActions userId={user?.id} {...props} />
                <Button
                    className={classNames("btn-round btn-icon shadow-none", retroAdjustments?.length === 0 && "border")}
                    size="sm"
                    style={retroAdjustments?.length > 0 ? { background: "#fff7ed" } : {}}
                    hidden={!canCreateRetro || !lockedPayGroupItems?.length || lockedPayGroupItems?.length === 0}
                    onClick={() => openRetroModal({ user, payGroupItems, mutateRetroAdjustment, refetchTimeSheets })}
                >
                    {t("retro-adjustment")}
                </Button>

                <Button
                    className="btn-round shadow-none"
                    size="sm"
                    style={{ background: "#e0d8fc77" }}
                    hidden={!retros?.length || retros?.length === 0}
                    onClick={() => openDetails({ data: retroAdjustments, user })}
                >
                    {t("retro-adjustment-details")}
                </Button>

                <Button
                    className="btn-round shadow-none"
                    size="sm"
                    style={{ background: "#bbd8fa77" }}
                    hidden={!byPayDateRetro?.length || byPayDateRetro?.length === 0}
                    onClick={() => openDetails({ data: byPayDateRetros, user })}
                >
                    {t("view-retros-by-pay-date")}
                </Button>
            </div>

            <div>
                {viewSummary && (
                    <FontAwesomeIcon
                        className="cursor-pointer mr-2"
                        style={{ fontSize: 15 }}
                        icon={faEye}
                        onClick={viewSummary}
                    />
                )}
                <Download
                    downloadSummary={downloadSummary}
                    downloadDetails={downloadDetails}
                />

                <ColumnToggle
                    prefix={`timesheet-user-${user?.id}`}
                    columns={columns}
                />
            </div>
        </div>
    );
}

export default Actions;
